import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cancelOssOrder,
  createOssChange,
  deleteOssOrder,
  deleteOssDocument,
  fetchOssChanges,
  fetchOssOrderDetail,
  getOssDocumentDownloadUrl,
  processOssAction,
  reviewOssChange,
  uploadOssDocument,
} from "../../api/ossApi";
import { displayLabel, orderStatus } from "../../constants/labels";
import { OssOrderFormDialog } from "../OssOrderFormDialog";
import { OssActionProcessDialog } from "../actions/OssActionProcessDialog";
import { OssChangeRequestDialog } from "../changes/OssChangeRequestDialog";
import { OssDocumentUploadDialog } from "../documents/OssDocumentUploadDialog";
import { ConfirmDialog } from "../orderDetail/OssOrderDetailDialogs";
import { OssOrderDetailWorkspace } from "./OssOrderDetailWorkspace";
import "../actions/OssActionProcessDialog.css";
import "./OssOrderInlineDetail.css";
import { isActionNeeded } from "../orderDetail/detailUtils";
import { OssLifecycleDialog } from "./OssLifecycleDialog";

export const OssOrderInlineDetail = ({ open, groupCode, projectCode, orderId, onClose, onChanged }) => (
  <OssLifecycleDialog {...{ open, groupCode, projectCode, orderId, onClose }} title="주문 관리" onSaved={onChanged} />
);

export const LegacyOssOrderInlineDetail = ({
  open,
  groupCode,
  projectCode,
  orderId,
  onClose,
  onChanged,
}) => {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changesLoading, setChangesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLine, setActionLine] = useState(null);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const load = useCallback(async () => {
    if (!open || !orderId) return;
    setLoading(true);
    setChangesLoading(true);
    setError("");
    setData(null);
    setChanges([]);
    try {
      const [detail, changeData] = await Promise.all([
        fetchOssOrderDetail(groupCode, projectCode, orderId),
        fetchOssChanges(groupCode, projectCode, { orderId, pageSize: 100 }),
      ]);
      setData(detail);
      setChanges(changeData?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || "주문 상세를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
      setChangesLoading(false);
    }
  }, [groupCode, open, orderId, projectCode]);

  useEffect(() => {
    if (!open) {
      setTab(0);
      setData(null);
      setChanges([]);
      setError("");
      setEditOpen(false);
      setCancelOpen(false);
      setDeleteOpen(false);
      setActionLine(null);
      setDocumentOpen(false);
      setChangeOpen(false);
      setDocumentToDelete(null);
      return;
    }
    load();
  }, [load, open]);

  const order = data?.order || {};
  const lines = useMemo(() => data?.lines || [], [data?.lines]);
  const disabledByCancel = order.order_status === "CANCELLED";
  const summary = useMemo(() => {
    const openLines = lines.filter((line) => line.line_status !== "CANCELLED");
    const target = openLines.length ? openLines : lines;
    const minMargin = target
      .map((line) => Number(line.available_margin_days))
      .filter(Number.isFinite)
      .sort((a, b) => a - b)[0];

    return {
      lineCount: target.length,
      actionCount: target.filter(isActionNeeded).length,
      earliestStart: target.map((line) => line.required_start_date).filter(Boolean).sort()[0],
      earliestCheck: target.map((line) => line.check_required_date).filter(Boolean).sort()[0],
      minMargin: minMargin ?? "-",
    };
  }, [lines]);

  const dialogOrders = useMemo(
    () =>
      lines.map((line) => ({
        ...line,
        order_id: order.order_id,
        order_no: order.order_no,
        customer_name: order.customer_name,
      })),
    [lines, order.customer_name, order.order_id, order.order_no]
  );

  const actionTicketTarget = useMemo(() => {
    if (!actionLine) return null;
    return {
      ...actionLine,
      order_id: order.order_id || orderId,
      order_no: order.order_no,
      customer_name: order.customer_name,
      site_name: order.site_name,
      exception_title: actionLine.risk_level === "OVERDUE" || actionLine.risk_level === "HIGH" ? "납기 또는 착수 기준 초과" : "일정 확인 필요",
      exception_message: "주문 상세에서 선택한 품목의 일정과 조치 내용을 확인합니다.",
    };
  }, [actionLine, order.customer_name, order.order_id, order.order_no, order.site_name, orderId]);

  const reloadAfterChange = async () => {
    await load();
    onChanged?.();
  };

  const submitCancel = async () => {
    const targetOrderId = order.order_id || orderId;
    if (!targetOrderId) return;
    setSubmitting(true);
    setError("");
    try {
      await cancelOssOrder(groupCode, projectCode, targetOrderId, {
        reason: "주문 상세 모달에서 주문 취소",
        userKey: "order.manager",
      });
      setCancelOpen(false);
      setToast("주문을 취소했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "주문 취소 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    const targetOrderId = order.order_id || orderId;
    if (!targetOrderId) return;
    setSubmitting(true);
    setError("");
    try {
      await deleteOssOrder(groupCode, projectCode, targetOrderId);
      setDeleteOpen(false);
      setToast("주문을 삭제했습니다.");
      onChanged?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.message || "주문 삭제 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAction = async (payload) => {
    if (payload.lifecycleSaved) { await reloadAfterChange().catch(() => {}); return true; }
    if (!actionLine) return false;
    setSubmitting(true);
    setError("");
    try {
      await processOssAction(groupCode, projectCode, actionLine.order_line_id, {
        ...payload,
        userKey: "order.manager",
      });
      setToast(payload.actionStatus === "NOTE" ? "조치 이력을 저장했습니다." : payload.nextCheckDate ? "다음 확인일을 예약했습니다." : "조치 처리를 완료했습니다.");
      await reloadAfterChange().catch(() => {});
      return true;
    } catch (e) {
      setError(e.response?.data?.message || "조치 처리 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const submitDocument = async (file, payload) => {
    setSubmitting(true);
    setError("");
    try {
      await uploadOssDocument(groupCode, projectCode, file, payload);
      setDocumentOpen(false);
      setToast("문서를 업로드했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "문서 업로드 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const submitDocumentDelete = async () => {
    if (!documentToDelete) return;
    setSubmitting(true);
    setError("");
    try {
      await deleteOssDocument(groupCode, projectCode, documentToDelete.document_id);
      setDocumentToDelete(null);
      setToast("문서를 삭제했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "문서 삭제 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const openDocument = (document) => {
    window.open(getOssDocumentDownloadUrl(groupCode, projectCode, document.document_id), "_blank", "noopener,noreferrer");
  };

  const submitChange = async (payload) => {
    setSubmitting(true);
    setError("");
    try {
      await createOssChange(groupCode, projectCode, payload);
      setChangeOpen(false);
      setToast("변경 요청을 등록했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "변경 요청 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const submitReview = async (row, status) => {
    setSubmitting(true);
    setError("");
    try {
      await reviewOssChange(groupCode, projectCode, row.change_request_id, {
        requestStatus: status,
        reviewedByUserKey: "order.manager",
        reviewNote: status === "APPROVED" ? "상세 모달에서 승인" : "상세 모달에서 반려",
      });
      setToast(status === "APPROVED" ? "변경 승인 및 일정 재계산을 완료했습니다." : "변경 요청을 반려했습니다.");
      await reloadAfterChange();
    } catch (e) {
      setError(e.response?.data?.message || "변경 검토 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth={false}
        aria-labelledby="oss-order-detail-title"
        PaperProps={{ className: "oss-action-dialog oss-order-detail-dialog" }}>
        <header className="oss-action-header">
          <span className="oss-action-heading-icon"><Inventory2OutlinedIcon /></span>
          <div className="oss-action-heading">
            <div className="oss-order-title-line">
              <h2 id="oss-order-detail-title">{order.order_no || "주문 상세"}</h2>
              {order.order_status && <span className="oss-order-status">{displayLabel(orderStatus, order.order_status)}</span>}
            </div>
            <p>{order.customer_name ? `${order.customer_name} · ${order.site_name || "납품처 미등록"}` : "주문 정보를 불러오는 중입니다."}</p>
          </div>
          <div className="oss-order-header-actions">
            <Button size="small" variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => setEditOpen(true)} disabled={submitting || disabledByCancel || !data}>수정</Button>
            <Button size="small" variant="outlined" color="warning" onClick={() => setCancelOpen(true)} disabled={submitting || disabledByCancel || !data}>주문 취소</Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => setDeleteOpen(true)} disabled={submitting || !data}>삭제</Button>
          </div>
          <Tooltip title="닫기"><IconButton aria-label="주문 상세 닫기" onClick={onClose} disabled={submitting}><CloseRoundedIcon /></IconButton></Tooltip>
        </header>
        <div className="oss-order-detail-body">
          {loading ? <Box className="oss-order-loading"><CircularProgress size={28} aria-label="주문 불러오는 중" /></Box>
            : error && !data ? <Alert severity="warning">{error}</Alert>
            : data ? <>
              {error && <Alert severity="warning">{error}</Alert>}
              <OssOrderDetailWorkspace
                order={order} summary={summary} tab={tab} onTabChange={setTab}
                lines={lines} data={data} changes={changes} changesLoading={changesLoading || submitting}
                disabledByCancel={disabledByCancel} submitting={submitting}
                onAction={setActionLine} onDocumentUpload={() => setDocumentOpen(true)}
                onDocumentOpen={openDocument} onDocumentDelete={setDocumentToDelete}
                onChangeRequest={() => setChangeOpen(true)} onReviewChange={submitReview}
              />
            </> : null}
        </div>
        <footer className="oss-action-footer">
          <Button variant="contained" onClick={onClose} disabled={submitting}>닫기</Button>
        </footer>
      </Dialog>

      <OssOrderFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        groupCode={groupCode}
        projectCode={projectCode}
        orderId={order.order_id || orderId}
        initialData={data}
        onSaved={async () => {
          setEditOpen(false);
          setToast("주문 정보를 수정했습니다.");
          await reloadAfterChange();
        }}
      />

      <ConfirmDialog
        open={cancelOpen}
        title="주문 취소"
        message="주문과 품목 일정을 취소 상태로 전환합니다. 취소된 주문은 일정 화면에서 제외됩니다."
        confirmLabel="취소 처리"
        loading={submitting}
        onClose={() => setCancelOpen(false)}
        onConfirm={submitCancel}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="주문 삭제"
        message="이 주문과 연결된 품목, 문서, 변경 요청, 이력이 함께 삭제됩니다. 잘못 등록한 주문인지 확인한 후 삭제해 주세요."
        confirmLabel="삭제"
        severity="error"
        loading={submitting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={submitDelete}
      />

      <OssActionProcessDialog
        open={Boolean(actionLine)}
        item={actionTicketTarget}
        groupCode={groupCode}
        projectCode={projectCode}
        loading={submitting}
        onClose={() => setActionLine(null)}
        onSubmit={submitAction}
      />
      <OssDocumentUploadDialog
        open={documentOpen}
        orders={dialogOrders}
        loading={submitting}
        onClose={() => setDocumentOpen(false)}
        onSubmit={submitDocument}
      />
      <OssChangeRequestDialog
        open={changeOpen}
        orders={dialogOrders}
        loading={submitting}
        onClose={() => setChangeOpen(false)}
        onSubmit={submitChange}
      />
      <ConfirmDialog
        open={Boolean(documentToDelete)}
        title="문서 삭제"
        message="선택한 주문 문서를 삭제합니다."
        confirmLabel="삭제"
        severity="error"
        loading={submitting}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={submitDocumentDelete}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};
